<template>
  <div class="page-shell">
    <section class="page-panel" v-loading="loading">
      <header class="page-header">
        <div class="page-header__copy">
          <div class="workspace-shortcuts">
            <el-button link type="primary" @click="router.push('/')">首页</el-button>
            <span>/</span>
            <el-button link @click="router.push('/dashboard')">项目工作台</el-button>
          </div>
          <p class="eyebrow">项目工作台</p>
          <h1>{{ project?.title || `项目 ${projectId}` }}</h1>
          <p class="page-subtitle">{{ pageSubtitle }}</p>
        </div>
        <div class="header-actions">
          <el-tag :type="stateTagType" size="large">{{ getWorkflowStateLabel(workflow?.state || 'draft') }}</el-tag>
          <el-button plain @click="router.push('/')">返回首页</el-button>
          <el-button @click="router.push('/dashboard')">返回项目列表</el-button>
        </div>
      </header>

      <section v-if="inspectableStageOptions.length" class="workspace-stage-rail">
        <ApprovalStageSwitcher
          :inspectable-stage-options="inspectableStageOptions"
          :current-stage="currentStage"
          :previous-inspectable-stage="previousInspectableStage"
          :next-inspectable-stage="nextInspectableStage"
          @switch-stage="handleSwitchStage($event as ApprovalStage)"
        ></ApprovalStageSwitcher>
      </section>

      <ProjectTimelinePanel
        :loading="projectTimelineLoading"
        :items="projectTimelineItems"
        :created-at="projectTimelineCreatedAt"
        :latest-updated-at="projectTimelineLatestUpdatedAt"
      ></ProjectTimelinePanel>

      <div class="content-grid">
        <div class="main-column">
          <StoryboardWorkspaceSection
            v-if="currentStage === 'storyboard'"
            :project-id="projectId"
            :inspectable-stage-options="inspectableStageOptions"
            :current-stage="currentStage"
            :previous-inspectable-stage="previousInspectableStage"
            :next-inspectable-stage="nextInspectableStage"
            :storyboard-workspace-tab="storyboardWorkspaceTab"
            :storyboard-saving="storyboardSaving"
            :acting="acting"
            :consistency-saving="consistencySaving"
            :is-pending-approval="workflow?.pending_approval?.stage === currentStage"
            :reviewing-previous-stage="currentStage === 'storyboard' && isReviewingPreviousStage"
            :show-resume-action="showResumeAction"
            :approve-button-label="approveButtonLabel"
            :storyboard-approval-hint="storyboardApprovalHint"
            :can-approve-current-stage="canApproveCurrentStage"
            :side-guidance="sideGuidance"
            :storyboard-draft-scenes="storyboardDraftScenes"
            :storyboard-visual-active-scene-number="storyboardVisualActiveSceneNumber"
            :storyboard-editing-scene-number="storyboardEditingSceneNumber"
            :consistency-asset-selection-options="consistencyAssetSelectionOptions"
            :build-scene-meta="getStoryboardSceneMeta"
            :build-scene-binding-labels="getStoryboardSceneBindingLabels"
            :consistency-asset-tab="consistencyAssetTab"
            :consistency-asset-stats="consistencyAssetStats"
            :active-consistency-assets="activeConsistencyAssets"
            :consistency-asset-image-generating="consistencyAssetImageGenerating"
            :consistency-asset-image-uploading="consistencyAssetImageUploading"
            :format-scene-image-source="formatSceneImageSource"
            :storyboard-generation-tab="storyboardGenerationTab"
            :selected-storyboard-scene-draft="selectedStoryboardSceneDraft"
            :selected-storyboard-image-url="selectedStoryboardImageUrl"
            :selected-storyboard-video-url="selectedStoryboardVideoUrl"
            :selected-storyboard-uploaded-video="selectedStoryboardUploadedVideo"
            :selected-storyboard-video-error="selectedStoryboardVideoError"
            :selected-scene-preview-overlay-visible="selectedScenePreviewOverlayVisible"
            :selected-scene-preview-overlay-title="selectedScenePreviewOverlayTitle"
            :selected-scene-preview-overlay-description="selectedScenePreviewOverlayDescription"
            :selected-scene-preview-overlay-note="selectedScenePreviewOverlayNote"
            :storyboard-generation-scene-items="storyboardGenerationSceneItems"
            :selected-storyboard-bound-assets="selectedStoryboardBoundAssets"
            :selected-storyboard-scene-number="selectedStoryboardSceneNumber"
            :selected-storyboard-prompt-mention-items="selectedStoryboardPromptMentionItems"
            :selected-scene-video-task="selectedSceneVideoTask"
            :selected-scene-video-task-description="selectedSceneVideoTaskDescription"
            :selected-scene-video-task-status-label="selectedSceneVideoTaskStatusLabel"
            :selected-scene-video-task-timeline="selectedSceneVideoTaskTimeline"
            :selected-scene-video-task-facts="selectedSceneVideoTaskFacts"
            :scene-video-feedback-drafts="sceneVideoFeedbackDrafts"
            :scene-video-loading="sceneVideoLoading"
            :scene-video-saving="sceneVideoSaving"
            :is-scene-image-loading="isSceneImageLoading"
            :is-scene-image-busy="isSceneImageBusy"
            :is-scene-video-generating="isSceneVideoGenerating"
            @update:storyboardWorkspaceTab="storyboardWorkspaceTab = $event"
            @switch-stage="handleSwitchStage($event as ApprovalStage)"
            @save-storyboard-scenes="handleSaveStoryboardScenes()"
            @save-consistency-assets="handleSaveConsistencyAssets()"
            @save-and-regenerate-storyboard="handleSaveAndRegenerateStoryboard"
            @add-storyboard-scene="handleAddStoryboardScene"
            @insert-storyboard-scene="handleInsertStoryboardScene($event.sceneNumber, $event.position)"
            @update:hoveredStoryboardSceneNumber="hoveredStoryboardSceneNumber = $event"
            @update:selectedStoryboardSceneNumber="selectedStoryboardSceneNumber = $event"
            @toggle-storyboard-scene-edit="handleToggleStoryboardSceneEdit"
            @delete-storyboard-scene="handleDeleteStoryboardScene"
            @mark-storyboard-draft-dirty="markStoryboardDraftDirty"
            @cancel-storyboard-scene-edit="handleCancelStoryboardSceneEdit"
            @finish-storyboard-scene-edit="handleFinishStoryboardSceneEdit"
            @update:consistencyAssetTab="consistencyAssetTab = $event"
            @add-consistency-asset="handleAddConsistencyAsset"
            @mark-consistency-dirty="markConsistencyDirty"
            @open-knowledge-base-for-consistency-asset="handleOpenKnowledgeBaseForConsistencyAsset($event.type, $event.id)"
            @generate-consistency-asset-reference="handleGenerateConsistencyAssetReference($event.type, $event.id)"
            @upload-consistency-asset="triggerConsistencyAssetUpload($event.type, $event.id)"
            @clear-consistency-asset-image="handleClearConsistencyAssetImage($event.type, $event.id)"
            @remove-consistency-asset="handleRemoveConsistencyAsset($event.type, $event.id)"
            @update:storyboardGenerationTab="storyboardGenerationTab = $event"
            @open-knowledge-base-for-scene="handleOpenKnowledgeBaseForScene"
            @create-mention-asset="handleCreatePromptMentionAsset($event.name, $event.sceneNumber)"
            @trigger-scene-file-upload="triggerSceneFileUpload"
            @clear-scene-image="handleClearSceneImage"
            @generate-scene-image="handleGenerateSceneImage($event.sceneNumber, $event.imageModelId)"
            @download-scene-image="handleDownloadAsset(selectedStoryboardImageUrl, `scene-${$event}.png`)"
            @scene-video-loaded="handleSceneVideoLoadedData"
            @scene-video-error="handleSceneVideoPlaybackError"
            @generate-scene-video="handleGenerateSceneVideo($event.sceneNumber, $event.videoModelId)"
            @trigger-scene-video-upload="triggerSceneVideoUpload"
            @download-scene-video="handleDownloadAsset(selectedStoryboardVideoUrl, `scene-${$event}.mp4`)"
            @save-scene-video-feedback="handleSaveSceneVideoFeedback"
            @approve-from-workspace="handleApproveFromWorkspace"
            @resume-from-workspace="handleResumeFromWorkspace"
          ></StoryboardWorkspaceSection>

          <FinalDeliveryWorkspace
            v-else-if="currentStage === 'final'"
            :effective-final-video-url="effectiveFinalVideoUrl"
            :system-final-video-url="finalStageVideoUrl"
            :final-video-player-error="finalVideoPlayerError"
            :final-stage-status-label="finalStageStatusLabel"
            :final-stage-video-message="finalStageVideoMessage"
            :final-stage-facts="finalStageFacts"
            :final-video-uploading="finalVideoUploading"
            :final-workspace-saving="finalWorkspaceSaving"
            :uploaded-final-video="uploadedFinalVideo"
            :audio-mix-preview-video="finalAudioPreviewVideo"
            :audio-mix-delivery-video="finalAudioDeliveryVideo"
            :preferred-final-video-source="preferredFinalVideoSource"
            :final-voiceover-mode="finalVoiceoverMode"
            :final-voiceover-tone="finalVoiceoverTone"
            :final-bgm-plan="finalBgmPlan"
            :final-voiceover-mode-options="finalVoiceoverModeOptions"
            :final-voiceover-tone-options="finalVoiceoverToneOptions"
            :final-bgm-plan-options="finalBgmPlanOptions"
            :final-tts-provider="finalTtsProvider"
            :final-tts-voice-id="finalTtsVoiceId"
            :final-tts-speed="finalTtsSpeed"
            :final-tts-script="finalTtsScript"
            :final-tts-start-ms="finalTtsStartMs"
            :audio-options-loading="audioOptionsLoading"
            :tts-generating="ttsGenerating"
            :bgm-uploading="bgmUploading"
            :final-audio-mixing="finalAudioMixing"
            :audio-mix-controls="audioMixControls"
            :tts-voice-options="ttsVoiceOptions"
            :final-tts-timeline-segments="finalTtsTimelineSegments"
            :uploaded-final-bgm-audio="uploadedFinalBgmAudio"
            :pending-approval="workflow?.pending_approval?.stage === currentStage"
            :review-feedback="reviewFeedback"
            :saved-review-feedback="persistedReviewFeedback"
            :approval-history-records="approvalHistoryRecords"
            :acting="acting"
            :can-request-revision="canRequestRevision"
            :can-approve-current-stage="canApproveCurrentStage"
            :review-feedback-placeholder="reviewFeedbackPlaceholder"
            :revision-action-label="revisionActionLabel"
            :approve-button-label="approveButtonLabel"
            :side-guidance="sideGuidance"
            :show-resume-action="showResumeAction"
            @download-final="downloadCurrentFinalVideo"
            @trigger-upload="triggerFinalVideoUpload"
            @save-workspace="handleSaveFinalWorkspace"
            @final-video-loaded="handleFinalVideoLoadedData"
            @final-video-error="handleFinalVideoPlaybackError"
            @update:finalVoiceoverMode="finalVoiceoverMode = $event"
            @update:finalVoiceoverTone="finalVoiceoverTone = $event"
            @update:finalBgmPlan="finalBgmPlan = $event"
            @update:finalTtsProvider="finalTtsProvider = $event"
            @update:finalTtsVoiceId="finalTtsVoiceId = $event"
            @update:finalTtsSpeed="finalTtsSpeed = $event"
            @update:finalTtsScript="finalTtsScript = $event"
            @update:finalTtsStartMs="finalTtsStartMs = $event"
            @update:ttsSegmentStartMs="handleUpdateTtsSegmentStartMs($event.id, $event.startMs)"
            @update:audioMixControls="audioMixControls = $event"
            @generate-tts="handleGenerateTts"
            @trigger-bgm-upload="triggerBgmUpload"
            @generate-mix-preview="handleGenerateMixPreview"
            @apply-preview-as-delivery="handleApplyPreviewAsDelivery"
            @use-system-as-delivery="handleUseSystemAsDelivery"
            @use-uploaded-as-delivery="handleUseUploadedAsDelivery"
            @use-audio-delivery="handleUseAudioMixDelivery"
            @clear-generated-tts="clearGeneratedTts"
            @remove-tts-segment="handleRemoveTtsSegment"
            @download-tts-segment="downloadTtsSegment"
            @clear-uploaded-bgm="clearUploadedBgm"
            @download-uploaded-bgm="downloadUploadedBgm"
            @download-preview-video="downloadPreviewVideo"
            @open-audio-library="handleOpenAudioLibrary"
            @update:reviewFeedback="reviewFeedback = $event"
            @request-revision="handleRequestRevision"
            @approve="handleApproveFromWorkspace"
            @resume="handleResumeFromWorkspace"
          ></FinalDeliveryWorkspace>

          <StageResultPanel
            v-else-if="showStageResultPanel"
            :current-stage-label="currentStageLabel"
            :can-edit-current-stage="canEditCurrentStage"
            :has-unsaved-edits="hasUnsavedEdits"
            :saving="saving"
            :inspectable-stage-options="inspectableStageOptions"
            :current-stage="currentStage"
            :previous-inspectable-stage="previousInspectableStage"
            :next-inspectable-stage="nextInspectableStage"
            :editor-hint="editorHint"
            :editor-content="editorContent"
            :show-approval-actions="showStageApprovalPanel"
            :pending-approval="workflow?.pending_approval?.stage === currentStage"
            :pending-approval-stage="workflow?.pending_approval?.stage || null"
            :reviewing-previous-stage="isReviewingPreviousStage"
            :revision-target-stage="revisionTargetStage"
            :review-feedback="reviewFeedback"
            :saved-review-feedback="persistedReviewFeedback"
            :approval-history-records="approvalHistoryRecords"
            :side-guidance="sideGuidance"
            :acting="acting"
            :can-request-revision="canRequestRevision"
            :can-approve-current-stage="canApproveCurrentStage"
            :review-feedback-placeholder="reviewFeedbackPlaceholder"
            :revision-action-label="revisionActionLabel"
            :approve-button-label="approveButtonLabel"
            :show-resume-action="showResumeAction"
            @cancel-edits="handleCancelEdits"
            @save-edits="handleSaveEdits"
            @switch-stage="handleSwitchStage($event as ApprovalStage)"
            @update:editorContent="editorContent = $event; markStageResultEditorDirty()"
            @update:reviewFeedback="reviewFeedback = $event"
            @request-revision="handleRequestRevision"
            @approve="handleApproveFromWorkspace"
            @resume="handleResumeFromWorkspace"
          ></StageResultPanel>
        </div>
      </div>

      <input
        ref="sceneUploadInput"
        class="storyboard-upload-input"
        type="file"
        accept="image/*"
        @change="handleSceneFileSelected"
      />
      <input
        ref="sceneVideoUploadInput"
        class="storyboard-upload-input"
        type="file"
        accept="video/*"
        @change="handleSceneVideoFileSelected"
      />
      <input
        ref="consistencyAssetUploadInput"
        class="storyboard-upload-input"
        type="file"
        accept="image/*"
        @change="handleConsistencyAssetFileSelected"
      />
      <input
        ref="finalVideoUploadInput"
        class="storyboard-upload-input"
        type="file"
        accept="video/*"
        @change="handleFinalVideoFileSelected"
      />
      <input
        ref="finalBgmUploadInput"
        class="storyboard-upload-input"
        type="file"
        accept="audio/*"
        @change="handleBgmFileSelected"
      />

      <WorkspaceTransientUi
        :scene-video-upload-dialog-visible="sceneVideoUploadDialogVisible"
        :scene-video-upload-feedback-draft="sceneVideoUploadFeedbackDraft"
        :is-workspace-busy="isWorkspaceBusy"
        :workspace-busy-title="workspaceBusyTitle"
        :workspace-busy-description="workspaceBusyDescription"
        :workspace-busy-stage-label="workspaceBusyStageLabel"
        @update:scene-video-upload-dialog-visible="sceneVideoUploadDialogVisible = $event"
        @update:scene-video-upload-feedback-draft="sceneVideoUploadFeedbackDraft = $event"
        @cancel-scene-video-upload-dialog="handleCancelSceneVideoUploadDialog"
        @confirm-scene-video-upload-dialog="handleConfirmSceneVideoUploadDialog"
      ></WorkspaceTransientUi>

      <StageAssistantFab
        :open="stageAssistantOpen"
        :draft="stageAssistantDraft"
        :loading="stageAssistantLoading"
        :stage-label="currentStageLabel"
        :title="stageAssistantTitle"
        :description="stageAssistantDescription"
        :helper-text="stageAssistantHelperText"
        :messages="stageAssistantMessages"
        @update:open="stageAssistantOpen = $event"
        @update:draft="stageAssistantDraft = $event"
        @submit="handleStageAssistantSubmit"
      ></StageAssistantFab>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'

import { materialsApi, projectApi, type Project } from '@/api'
import {
  approveStage,
  generateFinalAudioTts,
  getFinalAudioOptions,
  getWorkflow,
  mixFinalAudioIntoVideo,
  requestRevision,
  resumeWorkflow,
  startWorkflow,
  updateStageContent,
  updateWorkflowInputData,
} from '@/api/workflow'
import type { ApprovalStage, WorkflowInstance, WorkflowStageAssistantAction } from '@/types/workflow'
import type { WorkflowGenerationAction } from '@/utils/workflowGeneration'
import { consumeWorkflowKnowledgeAudioSelections } from '@/utils/workflowKnowledge'
import { buildCustomerPhaseProgress, getApprovalStageLabel, getWorkflowStateLabel } from '@/utils/workflowPresentation'
import ApprovalStageSwitcher from '@/views/customer/generation-workspace/ApprovalStageSwitcher.vue'
import FinalDeliveryWorkspace from '@/views/customer/generation-workspace/FinalDeliveryWorkspace.vue'
import ProjectTimelinePanel from '@/views/customer/generation-workspace/ProjectTimelinePanel.vue'
import StageAssistantFab from '@/views/customer/generation-workspace/StageAssistantFab.vue'
import StageResultPanel from '@/views/customer/generation-workspace/StageResultPanel.vue'
import StoryboardWorkspaceSection from '@/views/customer/generation-workspace/StoryboardWorkspaceSection.vue'
import WorkspaceTransientUi from '@/views/customer/generation-workspace/WorkspaceTransientUi.vue'
import {
  FINAL_BGM_PLAN_OPTIONS as finalBgmPlanOptions,
  FINAL_VOICEOVER_MODE_OPTIONS as finalVoiceoverModeOptions,
  FINAL_VOICEOVER_TONE_OPTIONS as finalVoiceoverToneOptions,
} from '@/views/customer/generation-workspace/sceneVideo'
import { buildStageAssistantRegistry } from '@/views/customer/generation-workspace/stageAssistantRegistry'
import { useApprovalFlowPolicy } from '@/views/customer/generation-workspace/useApprovalFlowPolicy'
import { useFinalStageWorkspace } from '@/views/customer/generation-workspace/useFinalStageWorkspace'
import { useGenerationWorkspaceCoreState } from '@/views/customer/generation-workspace/useGenerationWorkspaceCoreState'
import { useGenerationWorkspaceLifecycle } from '@/views/customer/generation-workspace/useGenerationWorkspaceLifecycle'
import { useProjectTimeline } from '@/views/customer/generation-workspace/useProjectTimeline'
import { useStageAssistant } from '@/views/customer/generation-workspace/useStageAssistant'
import { useStageResultWorkspace } from '@/views/customer/generation-workspace/useStageResultWorkspace'
import { useStoryboardStageWorkspace } from '@/views/customer/generation-workspace/useStoryboardStageWorkspace'
import { useWorkflowLiveSync } from '@/views/customer/generation-workspace/useWorkflowLiveSync'
import { useWorkspaceExecution } from '@/views/customer/generation-workspace/useWorkspaceExecution'
import { useWorkspacePageHelpers } from '@/views/customer/generation-workspace/useWorkspacePageHelpers'
import { useWorkspaceStatus } from '@/views/customer/generation-workspace/useWorkspaceStatus'

const STORYBOARD_WORKSPACE_INPUT_KEY = 'storyboard_workspace_state'

const route = useRoute()
const router = useRouter()

const projectId = Number(route.params.projectId)
const loading = ref(false)
const acting = ref(false)
const project = ref<Project | null>(null)
const workflow = ref<WorkflowInstance | null>(null)
const editorContent = ref('')
const editorDirty = ref(false)
const hasObservedWorkflowSnapshot = ref(false)
const lastShownWorkflowFailureKey = ref('')
const reviewFeedback = ref('')
const activeAction = ref<WorkflowGenerationAction | null>(null)
const sceneVideoFeedbackDrafts = ref<Record<number, string>>({})
const generatedSceneVideoOverrides = ref<Record<number, Record<string, any>>>({})
const workspaceBusyState = ref<{ title: string; description: string; stageLabel: string; notice?: string } | null>(null)
const finalVideoUploadInput = ref<HTMLInputElement | null>(null)
const finalBgmUploadInput = ref<HTMLInputElement | null>(null)

const {
  projectTimelineItems,
  projectTimelineCreatedAt,
  projectTimelineLatestUpdatedAt,
  projectTimelineLoading,
  loadProjectTimeline,
} = useProjectTimeline({
  projectId,
  showLoadError: (message) => ElMessage.error(message),
})

const {
  currentStage,
  currentStageLabel,
  displayStageLabel,
  finalStageVideoUrl,
  generatedSceneVideoMap,
  primaryStageContent,
  requestedStage,
  sceneVideoTaskMap,
  storyboardScenes,
  storyboardWorkspaceState,
  uploadedFinalVideo,
  uploadedSceneVideoMap,
} = useGenerationWorkspaceCoreState({
  storyboardWorkspaceInputKey: STORYBOARD_WORKSPACE_INPUT_KEY,
  routeStageValue: () => route.query.stage,
  workflow,
  acting,
  generatedSceneVideoOverrides,
})

const customerPhases = computed(() => buildCustomerPhaseProgress(workflow.value))
const pendingApprovalStage = computed<ApprovalStage | null>(() => workflow.value?.pending_approval?.stage || null)
function normalizeApprovalFeedback(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

const persistedReviewFeedback = computed(() => {
  const currentStageKey = String(currentStage.value || '').trim()
  if (!currentStageKey) {
    return ''
  }

  const workflowData = workflow.value?.data as Record<string, any> | undefined
  const inputData = workflowData?.input_data as Record<string, any> | undefined
  const stageHandoffNotes = inputData?.stage_handoff_notes as Record<string, any> | undefined
  const noteEntry = stageHandoffNotes?.[currentStageKey]
  const noteFeedback = typeof noteEntry === 'string'
    ? noteEntry.trim()
    : String(noteEntry?.feedback || '').trim()

  if (noteFeedback) {
    return noteFeedback
  }

  const lastApprovalData = workflowData?.last_approval_data as Record<string, any> | undefined
  if (String(lastApprovalData?.stage || '').trim() === currentStageKey) {
    const lastApprovalFeedback = String(lastApprovalData?.feedback || lastApprovalData?.comment || '').trim()
    if (lastApprovalFeedback) {
      return lastApprovalFeedback
    }
  }

  if (workflow.value?.pending_approval?.stage === currentStageKey) {
    return String(workflow.value.pending_approval.feedback || '').trim()
  }

  return ''
})
const approvalHistoryRecords = computed(() => {
  const activeStage = String(currentStage.value || '').trim()
  const records = (workflow.value?.logs || []).reduce<Array<{
    id: string
    stage: string
    stageLabel: string
    feedback: string
    timestamp: string
    title: string
  }>>((result, log) => {
    const metadata = (log.metadata || {}) as Record<string, any>
    const eventType = String(metadata.event_type || '').trim()
    const feedback = String(metadata.feedback || metadata.reason || '').trim()
    const stage = String(metadata.return_to_stage || metadata.stage || '').trim()
    if (!feedback || !stage || (activeStage && stage !== activeStage)) {
      return result
    }

    if (!['approval_recorded', 'revision_started', 'revision_completed', 'upstream_stage_reconfirmed'].includes(eventType)) {
      return result
    }

    const stageLabel = getApprovalStageLabel(stage)
    const title = eventType === 'approval_recorded'
      ? `${stageLabel}已确认`
      : eventType === 'upstream_stage_reconfirmed'
        ? `${stageLabel}已重新确认`
        : eventType === 'revision_completed'
          ? `${stageLabel}已重新生成`
          : `${stageLabel}已发起修改`

    result.push({
      id: `${log.id}-${eventType}-${stage}`,
      stage,
      stageLabel,
      feedback,
      timestamp: String(log.timestamp || ''),
      title,
    })
    return result
  }, [])

  const seenKeys = new Set<string>()

  return records.reverse().filter((record) => {
    const dedupeKey = [
      record.stage,
      record.title,
      normalizeApprovalFeedback(record.feedback),
    ].join('::')

    if (seenKeys.has(dedupeKey)) {
      return false
    }

    seenKeys.add(dedupeKey)
    return true
  })
})
const approvalStageOrder: ApprovalStage[] = ['proposal', 'script', 'storyboard', 'final']
const isReviewingPreviousStage = computed(() => {
  if (!pendingApprovalStage.value) {
    return false
  }

  return approvalStageOrder.indexOf(currentStage.value) >= 0
    && approvalStageOrder.indexOf(currentStage.value) < approvalStageOrder.indexOf(pendingApprovalStage.value)
})
const showStageApprovalPanel = computed(() => {
  if (currentStage.value === 'storyboard' || currentStage.value === 'final') {
    return false
  }

  if (pendingApprovalStage.value) {
    return true
  }

  if (workflow.value?.state === 'revision_in_progress') {
    return true
  }

  return workflow.value?.state === 'on_hold' || workflow.value?.state === 'failed'
})
const showResumeAction = computed(() => workflow.value?.state === 'on_hold' || workflow.value?.state === 'failed')

const {
  revisionActionLabel,
  revisionTargetStage,
  reviewFeedbackPlaceholder,
} = useApprovalFlowPolicy({
  currentStage,
  pendingApprovalStage,
  reviewingPreviousStage: isReviewingPreviousStage,
})

const {
  canInspectPhase,
  getActionErrorMessage,
  scrollWorkspaceToTop,
  stageStatus,
} = useWorkspacePageHelpers({
  currentStage,
  customerPhases,
  pendingApprovalStage,
  workflow: computed(() => workflow.value),
})

function markPageEditorDirty() {
  editorDirty.value = true
}

const {
  audioMixControls,
  audioOptionsLoading,
  bgmUploading,
  clearGeneratedTts,
  clearUploadedBgm,
  finalAudioDeliveryVideo,
  finalAudioMixing,
  finalAudioPreviewVideo,
  finalBgmPlan,
  finalTtsProvider,
  finalTtsScript,
  finalTtsStartMs,
  finalTtsSpeed,
  finalTtsVoiceId,
  finalVideoPlayerError,
  finalVideoUploading,
  finalVoiceoverDraft,
  finalVoiceoverMode,
  finalVoiceoverTone,
  finalWorkspaceSaving,
  finalTtsTimelineSegments,
  getStoryboardWorkspaceStatePayload,
  handleApplyPreviewAsDelivery,
  handleBgmFileSelected,
  handleDownloadAsset,
  handleFinalVideoFileSelected,
  handleFinalVideoLoadedData,
  handleFinalVideoPlaybackError,
  handleGenerateMixPreview,
  handleGenerateTts,
  handleOpenAudioLibrary,
  handleRemoveTtsSegment,
  handleSaveFinalWorkspace,
  handleUpdateTtsSegmentStartMs,
  handleUseAudioMixDelivery,
  handleUseSystemAsDelivery,
  handleUseUploadedAsDelivery,
  hydrateStoryboardWorkspaceState,
  persistStoryboardWorkspaceState,
  preferredFinalVideoSource,
  triggerBgmUpload,
  triggerFinalVideoUpload,
  ttsGenerating,
  ttsVoiceOptions,
  uploadedFinalBgmAudio,
} = useFinalStageWorkspace({
  projectId,
  routeFullPath: () => route.fullPath,
  routerPush: (location) => router.push(location),
  storyboardWorkspaceInputKey: STORYBOARD_WORKSPACE_INPUT_KEY,
  storyboardWorkspaceState,
  workflow,
  sceneVideoFeedbackDrafts,
  finalVideoUploadInput,
  finalBgmUploadInput,
  consumeQueuedKnowledgeAudioSelections: consumeWorkflowKnowledgeAudioSelections,
  updateWorkflowInputData,
  uploadMaterial: (formData, uploadOptions) => materialsApi.upload(formData, uploadOptions),
  getFinalAudioOptionsApi: getFinalAudioOptions,
  generateFinalAudioTtsApi: generateFinalAudioTts,
  mixFinalAudioIntoVideoApi: mixFinalAudioIntoVideo,
  getActionErrorMessage,
  success: (message) => ElMessage.success(message),
  error: (message) => ElMessage.error(message),
})

const effectiveFinalVideoUrl = computed(() => {
  if (preferredFinalVideoSource.value === 'audio_mix_delivery' && finalAudioDeliveryVideo.value?.url) {
    return finalAudioDeliveryVideo.value.url
  }
  if (preferredFinalVideoSource.value === 'uploaded_final' && uploadedFinalVideo.value?.url) {
    return uploadedFinalVideo.value.url
  }
  if (preferredFinalVideoSource.value === 'audio_mix_delivery' && uploadedFinalVideo.value?.url) {
    return uploadedFinalVideo.value.url
  }
  if (preferredFinalVideoSource.value === 'uploaded_final' && finalAudioDeliveryVideo.value?.url) {
    return finalAudioDeliveryVideo.value.url
  }
  return finalStageVideoUrl.value
})

const {
  activeConsistencyAssets,
  applyQueuedKnowledgeConsistencyAssetImages,
  applyQueuedKnowledgeSceneImages,
  consistencyAssetImageGenerating,
  consistencyAssetImageUploading,
  consistencyAssetSelectionOptions,
  consistencyAssetStats,
  consistencyAssetTab,
  consistencyAssetUploadInput,
  consistencyAssets,
  consistencySaving,
  ensureStoryboardScenesReady,
  formatSceneImageSource,
  getSceneVideoTaskStatus,
  getStoryboardSceneBindingLabels,
  getStoryboardSceneMeta,
  handleAddConsistencyAsset,
  handleAddStoryboardScene,
  handleCancelSceneVideoUploadDialog,
  handleCancelStoryboardSceneEdit,
  handleClearConsistencyAssetImage,
  handleClearSceneImage,
  handleConfirmSceneVideoUploadDialog,
  handleConsistencyAssetFileSelected,
  handleCreatePromptMentionAsset,
  handleDeleteStoryboardScene,
  handleFinishStoryboardSceneEdit,
  handleGenerateConsistencyAssetReference,
  handleGenerateSceneImage,
  handleGenerateSceneVideo,
  handleInsertStoryboardScene,
  handleOpenKnowledgeBaseForConsistencyAsset,
  handleOpenKnowledgeBaseForScene,
  handleRemoveConsistencyAsset,
  handleSaveAndRegenerateStoryboard,
  handleSaveConsistencyAssets,
  handleSaveSceneVideoFeedback,
  handleSaveStoryboardScenes,
  handleSceneFileSelected,
  handleSceneVideoFileSelected,
  handleSceneVideoLoadedData,
  handleSceneVideoPlaybackError,
  handleToggleStoryboardSceneEdit,
  hoveredStoryboardSceneNumber,
  hydrateKnowledgeContextsFromWorkflow,
  isSceneImageBusy,
  isSceneImageLoading,
  isSceneVideoGenerating,
  isSceneVideoTaskActive,
  markConsistencyDirty,
  markStoryboardDraftDirty,
  sceneUploadInput,
  sceneVideoGenerating,
  sceneVideoLoading,
  sceneVideoSaving,
  sceneVideoUploadDialogVisible,
  sceneVideoUploadFeedbackDraft,
  sceneVideoUploadInput,
  selectedScenePreviewOverlayDescription,
  selectedScenePreviewOverlayNote,
  selectedScenePreviewOverlayTitle,
  selectedScenePreviewOverlayVisible,
  selectedStoryboardBoundAssets,
  selectedStoryboardImageUrl,
  selectedStoryboardPromptMentionItems,
  selectedStoryboardSceneDraft,
  selectedStoryboardSceneNumber,
  selectedStoryboardUploadedVideo,
  selectedStoryboardVideoError,
  selectedStoryboardVideoUrl,
  selectedSceneVideoTask,
  selectedSceneVideoTaskDescription,
  selectedSceneVideoTaskFacts,
  selectedSceneVideoTaskStatusLabel,
  selectedSceneVideoTaskTimeline,
  stopAllSceneVideoTaskPolling,
  storyboardDraftScenes,
  storyboardEditingSceneNumber,
  storyboardGenerationSceneItems,
  storyboardGenerationTab,
  storyboardSaving,
  storyboardVisualActiveSceneNumber,
  storyboardWorkspaceTab,
  syncConsistencyAssetsFromStoryboard,
  syncQueuedKnowledgeContexts,
  syncSceneVideoTaskPolling,
  syncStoryboardDraftScenes,
  triggerConsistencyAssetUpload,
  triggerSceneFileUpload,
  triggerSceneVideoUpload,
} = useStoryboardStageWorkspace({
  projectId,
  currentStage,
  workflow,
  storyboardScenes,
  storyboardWorkspaceState,
  uploadedSceneVideoMap,
  generatedSceneVideoMap,
  sceneVideoTaskMap,
  sceneVideoFeedbackDrafts,
  generatedSceneVideoOverrides,
  workspaceBusyState,
  editorContent,
  editorDirty,
  reviewFeedback,
  getStoryboardWorkspaceStatePayload,
  persistStoryboardWorkspaceState,
  getActionErrorMessage,
  onRegenerateStoryboard: async (payload) => {
    await executeQueuedAction({
      intent: 'revise',
      stage: 'storyboard',
      feedback: payload.feedback,
      editedContent: payload.editedContent,
      createdAt: Date.now(),
    })
  },
  routeFullPath: () => route.fullPath,
  routerPush: (location) => router.push(location),
  markEditorDirty: markPageEditorDirty,
})

const finalStageFailureStage = computed(() => String(workflow.value?.data?.last_failed_stage || '').trim())
const {
  approveButtonLabel,
  canApproveCurrentStage,
  canRequestRevision,
  finalStageFacts,
  finalStageStatusLabel,
  finalStageVideoMessage,
  isWorkspaceBusy,
  pageSubtitle,
  sideGuidance,
  storyboardApprovalHint,
  stateTagType,
  workspaceBusyDescription,
  workspaceBusyStageLabel,
  workspaceBusyTitle,
} = useWorkspaceStatus({
  loading,
  acting,
  reviewFeedback,
  workflow,
  workspaceBusyState,
  activeAction,
  currentStage,
  currentStageLabel,
  displayStageLabel,
  finalStageFailureStage,
  finalStageVideoUrl,
  effectiveFinalVideoUrl,
  uploadedFinalVideo,
  deliveryFinalVideo: finalAudioDeliveryVideo,
  finalVoiceoverMode,
  finalVoiceoverTone,
  finalBgmPlan,
  finalVoiceoverModeOptions,
  finalVoiceoverToneOptions,
  finalBgmPlanOptions,
  storyboardScenes,
  storyboardDraftScenes,
  uploadedSceneVideoMap,
  generatedSceneVideoMap,
  sceneVideoGenerating,
  getSceneVideoTaskStatus,
  isSceneVideoTaskActive,
})

const {
  connectWorkflowSocket,
  disconnectWorkflowSocket,
  startPolling,
  stopPolling,
} = useWorkflowLiveSync({
  projectId,
  workflow,
})

const {
  canEditCurrentStage,
  editorHint,
  handleCancelEdits,
  handleSaveEdits,
  hasUnsavedEdits,
  inspectableStageOptions,
  markEditorDirty: markStageResultEditorDirty,
  nextInspectableStage,
  previousInspectableStage,
  saving,
  showStageResultPanel,
  syncEditorFromWorkflow,
} = useStageResultWorkspace({
  projectId,
  workflow,
  acting,
  currentStage,
  primaryStageContent,
  editorContent,
  editorDirty,
  canInspectPhase,
  stageStatus,
  updateStageContent,
  getActionErrorMessage,
  success: (message) => ElMessage.success(message),
  error: (message) => ElMessage.error(message),
})

const {
  handleSwitchStage,
  initializeWorkspace,
  loadBundle,
  syncRouteToWorkflowStage,
} = useGenerationWorkspaceLifecycle({
  projectId,
  requestedStage,
  loading,
  project,
  workflow,
  canInspectPhase,
  stageStatus,
  scrollWorkspaceToTop,
  routerReplace: (path) => router.replace(path),
  loadProject: () => projectApi.getById(projectId),
  loadWorkflow: () => getWorkflow(projectId),
  syncEditorFromWorkflow,
  hydrateKnowledgeContextsFromWorkflow,
  hydrateStoryboardWorkspaceState,
  syncConsistencyAssetsFromStoryboard,
  syncQueuedKnowledgeContexts,
  applyQueuedKnowledgeSceneImages,
  applyQueuedKnowledgeConsistencyAssetImages,
  connectWorkflowSocket,
  showLoadError: (message) => ElMessage.error(message),
})

const {
  consumeQueuedWorkspaceAction,
  executeQueuedAction,
  handleApproveFromWorkspace,
  handleRequestRevision,
  handleResumeFromWorkspace,
  stopLiveNarration,
} = useWorkspaceExecution({
  projectId,
  requestedStage,
  currentStage,
  canApproveCurrentStage,
  storyboardApprovalHint,
  revisionTargetStage,
  acting,
  activeAction,
  workflow,
  reviewFeedback,
  editorContent,
  ensureStoryboardScenesReady,
  handleSaveFinalWorkspace,
  syncEditorFromWorkflow,
  loadBundle,
  syncRouteToWorkflowStage,
  scrollWorkspaceToTop,
  startPolling,
  stopPolling,
  startWorkflowAction: (request) => startWorkflow(request),
  approveStageAction: (stage, payload) => approveStage(projectId, { stage, ...payload }),
  requestRevisionAction: (stage, payload) => requestRevision(projectId, { stage, ...payload }),
  resumeWorkflowAction: () => resumeWorkflow(projectId),
  routerPush: (path) => router.push(path),
  success: (message) => ElMessage.success(message),
  error: (message) => ElMessage.error(message),
  getActionErrorMessage,
})

function downloadCurrentFinalVideo() {
  handleDownloadAsset(effectiveFinalVideoUrl.value, `project-${projectId}-final.mp4`)
}

function downloadTtsSegment(segmentId: string) {
  const segment = finalTtsTimelineSegments.value.find((item) => item.id === segmentId)
  handleDownloadAsset(segment?.audio?.url || '', segment?.audio?.name || `project-${projectId}-tts.mp3`)
}

function downloadUploadedBgm() {
  handleDownloadAsset(uploadedFinalBgmAudio.value?.url || '', uploadedFinalBgmAudio.value?.name || `project-${projectId}-bgm.mp3`)
}

function downloadPreviewVideo() {
  handleDownloadAsset(finalAudioPreviewVideo.value?.url || '', finalAudioPreviewVideo.value?.name || `project-${projectId}-preview.mp4`)
}

async function handleStageAssistantSideEffect(action: WorkflowStageAssistantAction) {
  if (action.action_type !== 'regenerate_stage') {
    return { handled: false }
  }

  const reason = String(action.value || action.summary || '').trim() || '请根据最新修改重新生成'

  if (currentStage.value === 'storyboard') {
    reviewFeedback.value = reason
    await handleSaveAndRegenerateStoryboard()
    return { handled: true, message: '已按最新分镜文本重新触发分镜生成。' }
  }

  if (currentStage.value === 'proposal' || currentStage.value === 'script') {
    if (!workflow.value?.pending_approval?.stage) {
      return { handled: true, message: '当前不在审批节点，已先更新正文文本，尚未触发重新生成。' }
    }

    reviewFeedback.value = reason
    await executeQueuedAction({
      intent: 'revise',
      stage: workflow.value.pending_approval.stage as ApprovalStage,
      revisionTargetStage: currentStage.value,
      feedback: reason,
      editedContent: editorContent.value.trim() || undefined,
      createdAt: Date.now(),
    })
    return { handled: true, message: '已按更新后的正文重新触发当前阶段生成。' }
  }

  if (currentStage.value === 'final') {
    return { handled: true, message: '终版阶段已先更新文本内容；当前阶段不直接重生成成片，如需返工会回到上游阶段。' }
  }

  return { handled: false }
}

const {
  open: stageAssistantOpen,
  draft: stageAssistantDraft,
  loading: stageAssistantLoading,
  messages: stageAssistantMessages,
  title: stageAssistantTitle,
  description: stageAssistantDescription,
  helperText: stageAssistantHelperText,
  submit: handleStageAssistantSubmit,
} = useStageAssistant({
  projectId,
  currentStage,
  currentStageLabel,
  buildRegistry: (instruction) => buildStageAssistantRegistry({
    currentStage: currentStage.value,
    currentStageLabel: currentStageLabel.value,
    instruction,
    editorContent,
    reviewFeedback,
    markEditorDirty: markPageEditorDirty,
    storyboardWorkspaceTab,
    storyboardGenerationTab,
    consistencyAssetTab,
    selectedStoryboardSceneNumber,
    storyboardEditingSceneNumber,
    storyboardDraftScenes,
    markStoryboardDraftDirty,
    consistencyAssets,
    markConsistencyDirty,
    finalVoiceoverMode,
    finalVoiceoverTone,
    finalBgmPlan,
    finalVoiceoverDraft,
    finalTtsScript,
    finalVoiceoverModeOptions,
    finalVoiceoverToneOptions,
    finalBgmPlanOptions,
  }),
  runSideEffectAction: handleStageAssistantSideEffect,
})

onMounted(async () => {
  await initializeWorkspace(consumeQueuedWorkspaceAction)
  await loadProjectTimeline({ showError: false })
})

watch(
  () => route.fullPath,
  async () => {
    await consumeQueuedWorkspaceAction()
  }
)

watch(
  currentStage,
  () => {
    if (!acting.value) {
      syncEditorFromWorkflow(true)
    }
  }
)

watch(
  storyboardScenes,
  (scenes) => {
    syncStoryboardDraftScenes(scenes)
  },
  { immediate: true }
)

watch(
  pendingApprovalStage,
  async (stage, previousStage) => {
    if (!stage || stage === previousStage) {
      return
    }

    // Preserve an explicitly requested stage on the initial page load.
    // Otherwise a refresh can snap the route back to the pending approval stage
    // before the user sees the stage they were already inspecting.
    if (!previousStage && requestedStage.value) {
      return
    }

    await syncRouteToWorkflowStage(stage)
  }
)

watch(
  () => workflow.value?.updated_at,
  async (updatedAt, previousUpdatedAt) => {
    if (!updatedAt || updatedAt === previousUpdatedAt) {
      return
    }

    const failedStage = String(workflow.value?.data?.last_failed_stage || '').trim()
    const failureError = String(workflow.value?.data?.failure_error || '').trim()
    const failedAt = String(workflow.value?.data?.revision_context?.failed_at || '').trim()
    const failureKey = [failedStage, failureError, failedAt].filter(Boolean).join('::')

    if (!hasObservedWorkflowSnapshot.value) {
      hasObservedWorkflowSnapshot.value = true
      lastShownWorkflowFailureKey.value = failureKey
    } else if (failureKey && failureKey !== lastShownWorkflowFailureKey.value) {
      lastShownWorkflowFailureKey.value = failureKey
      ElMessage.error(failureError || '重新生成失败，请稍后重试')
    }

    if (!acting.value) {
      syncEditorFromWorkflow(false)
    }

    await loadProjectTimeline({ showError: false, silent: true })
  }
)

watch(
  sceneVideoTaskMap,
  (taskMap) => {
    syncSceneVideoTaskPolling(taskMap)
  },
  { deep: true, immediate: true }
)

onBeforeUnmount(() => {
  stopPolling()
  stopAllSceneVideoTaskPolling()
  stopLiveNarration()
  disconnectWorkflowSocket()
})
</script>

<style scoped src="./generation-workspace/GenerationWorkspacePage.css"></style>
